import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

import { getConfig } from '@/app/config'
import { keys } from '@/app/i18n'
import { ROUTE_NOT_FOUND } from '@/app/router/names'
import { useAuthStore } from '@/app/stores/auth'

/**
 * Projects (docs/projects): the `pm` software for Client and Staff, and the employee's projects in `em`. Each view is
 * a phase 0 stub showing the contract placeholder until its phase 1 package builds it (docs/projects/BRIEF.md); this
 * file and nav.ts belong to the orchestrator, so the packages never edit them. All behind the beta flag, checked when
 * the route is entered (route files are evaluated before `loadConfig()` runs, as ai-mode/routes.ts). `/pm/dashboard`
 * is the software's home (the home rule builds `/<software>/dashboard`): Projects has no dashboard, it opens the list.
 */
const betaOnly = () => (getConfig().betaFeature ? true : { name: ROUTE_NOT_FOUND })
/**
 * The employee's pages need Projects in the current workspace (`ProjectsEnabled`, API.md §8) — the same rule as their
 * menu entries (nav.ts); a Freelancer has no `pm` software for the guard to check (PM-4's note, wave 2).
 */
const projectsEnabledOnly = () =>
  Number(useAuthStore().session?.currentWorkspace?.ProjectsEnabled) === 1 ? true : { name: ROUTE_NOT_FOUND }
const listView = () => import('./views/ProjectListView.vue')
const editView = () => import('./views/ProjectEditView.vue')
const projectView = () => import('./views/ProjectView.vue')
const myWorkView = () => import('./views/MyWorkView.vue')

/** `create-project?kind=template` (the list's and settings' links) opens the template route, which has its own title. */
const toTemplateRoute = (to: RouteLocationNormalized) => {
  if (to.query.kind !== 'template') return true
  const { kind: _kind, ...query } = to.query
  return { name: 'create-template', query }
}

const projectsCrumb = { text: keys.t('projects.route.title.projects'), to: { name: 'projects' } }
const emProjectsCrumb = { text: keys.t('projects.route.title.projects'), to: { name: 'em-projects' } }

const routes: RouteRecordRaw[] = [
  { path: '/pm/dashboard', name: 'pm-dashboard', redirect: { name: 'projects' } },
  {
    path: '/pm/projects',
    name: 'projects',
    component: listView,
    beforeEnter: betaOnly,
    meta: {
      software: 'pm',
      resource: 'projects',
      pageTitle: keys.t('projects.route.title.projects'),
      breadcrumb: [{ text: keys.t('projects.route.title.projects'), active: true }],
    },
  },
  {
    path: '/pm/projects/new',
    name: 'create-project',
    component: editView,
    beforeEnter: [betaOnly, toTemplateRoute],
    meta: {
      software: 'pm',
      resource: 'projects',
      action: 'write',
      navActiveLink: 'projects',
      pageTitle: keys.t('projects.route.title.newProject'),
      breadcrumb: [projectsCrumb, { text: keys.t('projects.route.title.newProject'), active: true }],
    },
  },
  {
    // A new template (WAVE1 Q13): its own title and crumbs; `/pm/projects/new?kind=template` links land here.
    path: '/pm/projects/new-template',
    name: 'create-template',
    component: editView,
    beforeEnter: betaOnly,
    meta: {
      software: 'pm',
      resource: 'projects',
      action: 'write',
      navActiveLink: 'projects',
      pageTitle: keys.t('projects.route.title.newTemplate'),
      breadcrumb: [projectsCrumb, { text: keys.t('projects.route.title.newTemplate'), active: true }],
    },
  },
  {
    // `tab`: the project's sections (phase 1: tasks, time); the task panel is `?task=<id>`, the view `?view=board`.
    path: '/pm/projects/:id(\\d+)/:tab(tasks|time)?',
    name: 'project-view',
    component: projectView,
    beforeEnter: betaOnly,
    meta: {
      software: 'pm',
      resource: 'projects',
      navActiveLink: 'projects',
      pageTitle: keys.t('projects.route.title.project'),
      breadcrumb: [projectsCrumb, { text: keys.t('projects.route.title.project'), active: true }],
    },
  },
  {
    path: '/pm/projects/:id(\\d+)/edit',
    name: 'edit-project',
    component: editView,
    beforeEnter: betaOnly,
    meta: {
      software: 'pm',
      resource: 'projects',
      action: 'write',
      navActiveLink: 'projects',
      pageTitle: keys.t('projects.route.title.editProject'),
      breadcrumb: [projectsCrumb, { text: keys.t('projects.route.title.editProject'), active: true }],
    },
  },
  {
    path: '/pm/my-work',
    name: 'pm-my-work',
    component: myWorkView,
    beforeEnter: betaOnly,
    meta: {
      software: 'pm',
      resource: 'projects',
      pageTitle: keys.t('projects.route.title.myWork'),
      breadcrumb: [{ text: keys.t('projects.route.title.myWork'), active: true }],
    },
  },
  {
    // Everybody logs time (D5): no ACL resource beyond being signed in to the software.
    path: '/pm/time',
    name: 'pm-time',
    component: () => import('./views/TimeView.vue'),
    beforeEnter: betaOnly,
    meta: {
      software: 'pm',
      resource: 'always',
      pageTitle: keys.t('projects.route.title.time'),
      breadcrumb: [{ text: keys.t('projects.route.title.time'), active: true }],
    },
  },

  {
    // Stages, categories, task labels, job types (API.md §5); writes need `projects` write, rates `projects-finance`.
    path: '/pm/project-settings',
    name: 'project-settings',
    component: () => import('./views/ProjectSettingsView.vue'),
    beforeEnter: betaOnly,
    meta: {
      software: 'pm',
      resource: 'projects',
      pageTitle: keys.t('projects.route.title.projectSettings'),
      breadcrumb: [{ text: keys.t('projects.route.title.projectSettings'), active: true }],
    },
  },

  // --- the employee's side (em): the projects they are a member of, their work -------------------------------------
  {
    path: '/em/projects',
    name: 'em-projects',
    component: listView,
    beforeEnter: [betaOnly, projectsEnabledOnly],
    meta: {
      software: 'em',
      resource: 'always',
      pageTitle: keys.t('projects.route.title.projects'),
      breadcrumb: [{ text: keys.t('projects.route.title.projects'), active: true }],
    },
  },
  {
    path: '/em/projects/:id(\\d+)/:tab(tasks|time)?',
    name: 'em-project-view',
    component: projectView,
    beforeEnter: [betaOnly, projectsEnabledOnly],
    meta: {
      software: 'em',
      resource: 'always',
      navActiveLink: 'em-projects',
      pageTitle: keys.t('projects.route.title.project'),
      breadcrumb: [emProjectsCrumb, { text: keys.t('projects.route.title.project'), active: true }],
    },
  },
  {
    path: '/em/my-work',
    name: 'em-my-work',
    component: myWorkView,
    beforeEnter: [betaOnly, projectsEnabledOnly],
    meta: {
      software: 'em',
      resource: 'always',
      pageTitle: keys.t('projects.route.title.myWork'),
      breadcrumb: [{ text: keys.t('projects.route.title.myWork'), active: true }],
    },
  },
]

export default routes
